import ExcelJS from "exceljs";

import { ALLOWED_CHEMICAL_CATEGORIES } from "../../../constants/global.constants";

const ALLOWED_CIP_CATEGORIES = new Set(ALLOWED_CHEMICAL_CATEGORIES);
const CATEGORY_TO_ROW = {
  acid: 2,
  alkali: 3,
  cip_additive: 4,
};

const XLSX_MIME_TYPE =
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

const normalize = (value) =>
  String(value || "")
    .toLowerCase()
    .trim();
const unique = (values) => [...new Set(values)];

const asText = (value) => {
  if (value == null) return "";
  if (typeof value !== "object") return String(value);
  if (Array.isArray(value.richText)) {
    return value.richText.map((item) => item?.text || "").join("");
  }
  if (value.text) return value.text;
  if (value.result != null) return String(value.result);
  return String(value);
};

const toCsvList = (value) =>
  asText(value)
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);

const orderDisplayNames = (displayNames) => {
  const firstSymbolOrder = {};

  return unique(displayNames)
    .map((value, index) => {
      const raw = String(value || "").trim();
      const match = raw.match(/^(.*)\(([-+]?\d*\.?\d+)\)$/);
      const symbol = match ? normalize(match[1]) : normalize(raw);
      const concentration = match ? Number(match[2]) : Number.POSITIVE_INFINITY;

      if (firstSymbolOrder[symbol] === undefined) {
        firstSymbolOrder[symbol] = index;
      }

      return { raw, symbol, concentration, index };
    })
    .sort(
      (a, b) =>
        firstSymbolOrder[a.symbol] - firstSymbolOrder[b.symbol] ||
        a.concentration - b.concentration ||
        a.index - b.index,
    )
    .map((item) => item.raw);
};

const triggerLinkDownload = (href, fileName) => {
  const a = document.createElement("a");
  a.href = href;
  a.download = fileName;
  a.click();
};

export const downloadBulkCIPTemplate = async ({
  chemicalLibrary = [],
  templatePath,
  templateFileName,
}) => {
  const downloadStaticTemplate = () =>
    triggerLinkDownload(templatePath, templateFileName);

  try {
    const customChemicalsByRow = {};

    chemicalLibrary.forEach((chemical) => {
      if (!chemical?.is_custom) return;

      const category = normalize(chemical?.category);
      if (!ALLOWED_CIP_CATEGORIES.has(category)) return;

      const rowNumber = CATEGORY_TO_ROW[category];
      const chemicalName = String(chemical?.chemical || "").trim();
      const displayName = String(chemical?.display_name || "").trim();
      if (!rowNumber || (!chemicalName && !displayName)) return;

      const rowData =
        customChemicalsByRow[rowNumber] ||
        (customChemicalsByRow[rowNumber] = { chemicals: [], displayNames: [] });

      if (chemicalName) {
        rowData.chemicals.push(chemicalName);
      }

      if (displayName) {
        rowData.displayNames.push(displayName);
      }
    });

    if (Object.keys(customChemicalsByRow).length === 0) {
      downloadStaticTemplate();
      return;
    }

    const response = await fetch(templatePath);
    if (!response.ok) {
      downloadStaticTemplate();
      return;
    }

    const arrayBuffer = await response.arrayBuffer();
    const workbook = new ExcelJS.Workbook();
    await workbook.xlsx.load(arrayBuffer);

    const worksheet =
      workbook.getWorksheet("Chemicals") || workbook.worksheets[1];
    if (!worksheet) {
      downloadStaticTemplate();
      return;
    }

    const normalizeHeader = (value) =>
      normalize(asText(value)).replace(/\s+/g, "");
    const headerValues = worksheet.getRow(1).values || [];
    const chemicalsColumn = headerValues.findIndex(
      (value) => normalizeHeader(value) === "chemicals",
    );
    const displayNamesColumn = headerValues.findIndex(
      (value) => normalizeHeader(value) === "displaynames",
    );

    if (chemicalsColumn < 1 || displayNamesColumn < 1) {
      downloadStaticTemplate();
      return;
    }

    Object.entries(customChemicalsByRow).forEach(([rowNumber, rowData]) => {
      const chemicalsCell = worksheet.getCell(
        Number(rowNumber),
        chemicalsColumn,
      );
      chemicalsCell.value = unique([
        ...toCsvList(chemicalsCell.value),
        ...rowData.chemicals,
      ]).join(", ");

      const displayNamesCell = worksheet.getCell(
        Number(rowNumber),
        displayNamesColumn,
      );

      const mergedDisplayNames = unique([
        ...toCsvList(displayNamesCell.value),
        ...rowData.displayNames,
      ]);

      displayNamesCell.value = orderDisplayNames(mergedDisplayNames).join(", ");
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const blobUrl = URL.createObjectURL(
      new Blob([buffer], { type: XLSX_MIME_TYPE }),
    );
    triggerLinkDownload(blobUrl, templateFileName);
    URL.revokeObjectURL(blobUrl);
  } catch (error) {
    downloadStaticTemplate();
  }
};
